import QRCode from "qrcode";
import { cashiLogo } from "./cashiBrand";

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function fillRound(ctx, x, y, w, h, r, fill) {
  roundRect(ctx, x, y, w, h, r);
  ctx.fillStyle = fill;
  ctx.fill();
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function nameCase(value) {
  return String(value || "").trim().toLowerCase().replace(/(^|\s|-)([a-z])/g, (_, sep, letter) => `${sep}${letter.toUpperCase()}`);
}

function wrap(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else line = next;
  });
  if (line) lines.push(line);
  return lines.slice(0, 2);
}

function drawImageCover(ctx, image, x, y, w, h) {
  const scale = Math.max(w / image.width, h / image.height);
  const dw = image.width * scale;
  const dh = image.height * scale;
  ctx.drawImage(image, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

function drawMandala(ctx, cx, cy, size, color) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2;
  ctx.globalAlpha = 0.45;
  for (let ring = 1; ring <= 4; ring += 1) {
    ctx.beginPath();
    ctx.arc(0, 0, (size / 4) * ring, 0, Math.PI * 2);
    ctx.stroke();
  }
  const petals = 12;
  for (let i = 0; i < petals; i += 1) {
    const angle = (i / petals) * Math.PI * 2;
    ctx.beginPath();
    ctx.ellipse(Math.cos(angle) * size * 0.42, Math.sin(angle) * size * 0.42, size * 0.12, size * 0.22, angle, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

function drawConfetti(ctx, width, top, height) {
  const bits = ["#ef4444", "#f59e0b", "#21a7ec", "#a855f7", "#22c55e", "#f472b6"];
  for (let i = 0; i < 42; i += 1) {
    const x = 90 + ((i * 97) % (width - 180));
    const y = top + ((i * 37) % height);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate((i * 23) * Math.PI / 180);
    ctx.fillStyle = bits[i % bits.length];
    ctx.globalAlpha = 0.7;
    if (i % 3 === 0) {
      ctx.beginPath();
      ctx.arc(0, 0, 3.2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillRect(-5, -2, 10, 4);
    }
    ctx.restore();
  }
}

function drawDiyas(ctx, y, width) {
  const spots = [118, width - 118];
  spots.forEach((x) => {
    ctx.fillStyle = "rgba(180, 83, 9, 0.35)";
    ctx.beginPath();
    ctx.ellipse(x, y + 10, 22, 8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(245, 158, 11, 0.7)";
    ctx.beginPath();
    ctx.ellipse(x, y - 6, 6, 12, 0, 0, Math.PI * 2);
    ctx.fill();
  });
}

function drawStubDancers(ctx, x, y) {
  ctx.save();
  ctx.strokeStyle = "#d4a017";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.globalAlpha = 0.7;
  ctx.beginPath();
  ctx.arc(x + 28, y + 18, 8, 0, Math.PI * 2);
  ctx.moveTo(x + 28, y + 28);
  ctx.quadraticCurveTo(x + 18, y + 48, x + 12, y + 78);
  ctx.moveTo(x + 28, y + 36);
  ctx.quadraticCurveTo(x + 8, y + 30, x + 2, y + 18);
  ctx.moveTo(x + 28, y + 36);
  ctx.quadraticCurveTo(x + 48, y + 28, x + 58, y + 44);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x + 78, y + 22, 8, 0, Math.PI * 2);
  ctx.moveTo(x + 78, y + 32);
  ctx.quadraticCurveTo(x + 92, y + 54, x + 86, y + 82);
  ctx.moveTo(x + 78, y + 40);
  ctx.quadraticCurveTo(x + 108, y + 24, x + 118, y + 12);
  ctx.moveTo(x + 78, y + 42);
  ctx.quadraticCurveTo(x + 64, y + 58, x + 52, y + 78);
  ctx.stroke();
  ctx.restore();
}

export async function buildTicketCard({ title, name, mobile, code, image, txnid }) {
  let ticketLogo = null;
  if (image) {
    try { ticketLogo = await loadImage(image); } catch { ticketLogo = null; }
  }
  let cashi = null;
  try { cashi = await loadImage(cashiLogo); } catch { cashi = null; }

  const width = 780;
  const height = 1180;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  const frame = ctx.createLinearGradient(0, 0, width, height);
  frame.addColorStop(0, "#3b82f6");
  frame.addColorStop(0.45, "#7c3aed");
  frame.addColorStop(1, "#ec4899");
  fillRound(ctx, 0, 0, width, height, 42, frame);

  const inset = 14;
  const innerX = inset;
  const innerY = inset;
  const innerW = width - inset * 2;
  const innerH = height - inset * 2;
  fillRound(ctx, innerX, innerY, innerW, innerH, 32, "#fbf6ea");

  const headerH = 152;
  ctx.save();
  roundRect(ctx, innerX, innerY, innerW, innerH, 32);
  ctx.clip();
  const night = ctx.createLinearGradient(innerX, innerY, innerX + innerW, innerY + headerH);
  night.addColorStop(0, "#07111c");
  night.addColorStop(1, "#12263a");
  ctx.fillStyle = night;
  ctx.fillRect(innerX, innerY, innerW, headerH);
  drawMandala(ctx, innerX + 70, innerY + 36, 80, "rgba(255,255,255,0.12)");
  drawMandala(ctx, innerX + innerW - 70, innerY + 32, 72, "rgba(255,255,255,0.12)");
  ctx.restore();

  if (cashi) {
    const markH = 72;
    const markW = markH * (cashi.width / cashi.height);
    ctx.drawImage(cashi, (width - markW) / 2, innerY + 22, markW, markH);
  } else {
    ctx.fillStyle = "#21a7ec";
    ctx.font = "800 48px Manrope, Helvetica, Arial";
    ctx.textAlign = "center";
    ctx.fillText("CASHI", width / 2, innerY + 72);
  }
  ctx.fillStyle = "#8fd4f8";
  ctx.font = "700 18px Manrope, Helvetica, Arial";
  ctx.textAlign = "center";
  ctx.fillText("cashi.in", width / 2, innerY + 118);

  const bodyTop = innerY + headerH;
  const logoBandH = ticketLogo ? 188 : 12;
  drawMandala(ctx, innerX + 90, bodyTop + logoBandH + 70, 110, "#d4a017");
  drawMandala(ctx, innerX + innerW - 90, bodyTop + logoBandH + 280, 130, "#d4a017");
  drawConfetti(ctx, width, bodyTop + logoBandH + 8, 170);
  drawDiyas(ctx, bodyTop + logoBandH + 168, width);

  if (ticketLogo) {
    ctx.save();
    roundRect(ctx, innerX, innerY, innerW, innerH, 32);
    ctx.clip();
    ctx.beginPath();
    ctx.rect(innerX, bodyTop, innerW, logoBandH);
    ctx.clip();
    drawImageCover(ctx, ticketLogo, innerX, bodyTop, innerW, logoBandH);
    ctx.restore();
  }

  ctx.textAlign = "center";
  ctx.fillStyle = "#111827";
  ctx.font = "800 36px Manrope, Helvetica, Arial";
  const titleY = bodyTop + logoBandH + 46;
  const titleLines = wrap(ctx, title || "Lucky Draw Ticket", innerW - 80);
  titleLines.forEach((line, index) => ctx.fillText(line, width / 2, titleY + index * 40));

  ctx.fillStyle = "#c026d3";
  ctx.font = "700 18px Manrope, Helvetica, Arial";
  ctx.fillText("✦ ✦ ✦", width / 2, titleY + titleLines.length * 40 + 8);

  const qrSize = 268;
  const qrX = (width - qrSize) / 2;
  const qrY = titleY + titleLines.length * 40 + 32;
  const framePad = 16;
  const qg = ctx.createLinearGradient(qrX - framePad, qrY - framePad, qrX + qrSize + framePad, qrY + qrSize + framePad);
  qg.addColorStop(0, "#21a7ec");
  qg.addColorStop(1, "#7c3aed");
  fillRound(ctx, qrX - framePad, qrY - framePad, qrSize + framePad * 2, qrSize + framePad * 2, 22, qg);
  fillRound(ctx, qrX - 8, qrY - 8, qrSize + 16, qrSize + 16, 16, "#ffffff");
  const qrUrl = await QRCode.toDataURL(String(code || "TICKET"), { margin: 1, width: 680, color: { dark: "#0a0a0a", light: "#ffffff" } });
  const qr = await loadImage(qrUrl);
  ctx.drawImage(qr, qrX, qrY, qrSize, qrSize);

  const paidY = qrY + qrSize + 36;
  fillRound(ctx, width / 2 - 62, paidY - 18, 124, 36, 18, "#16a34a");
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(width / 2 - 38, paidY, 9, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#16a34a";
  ctx.lineWidth = 2.4;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(width / 2 - 43, paidY);
  ctx.lineTo(width / 2 - 39, paidY + 4);
  ctx.lineTo(width / 2 - 32, paidY - 4);
  ctx.stroke();
  ctx.fillStyle = "#fff";
  ctx.font = "800 18px Manrope, Helvetica, Arial";
  ctx.textAlign = "left";
  ctx.fillText("Paid", width / 2 - 22, paidY + 6);

  const stubY = height - inset - 228;
  ctx.save();
  roundRect(ctx, innerX, stubY, innerW, height - inset - stubY, 32);
  ctx.clip();
  ctx.fillStyle = "#f8fafc";
  ctx.fillRect(innerX, stubY, innerW, height - inset - stubY + 8);
  ctx.restore();

  ctx.save();
  const notch = ctx.createLinearGradient(0, stubY, width, stubY + 40);
  notch.addColorStop(0, "#3b82f6");
  notch.addColorStop(0.45, "#7c3aed");
  notch.addColorStop(1, "#ec4899");
  ctx.fillStyle = notch;
  ctx.beginPath();
  ctx.arc(inset, stubY + 18, 18, 0, Math.PI * 2);
  ctx.arc(width - inset, stubY + 18, 18, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.strokeStyle = "#cbd5e1";
  ctx.setLineDash([8, 8]);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(innerX + 28, stubY + 18);
  ctx.lineTo(innerX + innerW - 28, stubY + 18);
  ctx.stroke();
  ctx.restore();

  ctx.textAlign = "left";
  ctx.fillStyle = "#111827";
  ctx.font = "800 30px Manrope, Helvetica, Arial";
  ctx.fillText(nameCase(name) || "Customer", innerX + 36, stubY + 62);
  ctx.fillStyle = "#64748b";
  ctx.font = "650 18px Manrope, Helvetica, Arial";
  ctx.fillText(mobile || "", innerX + 36, stubY + 90);
  ctx.fillStyle = "#21a7ec";
  ctx.font = "800 22px Manrope, Helvetica, Arial";
  ctx.fillText(String(code || ""), innerX + 36, stubY + 122);
  if (txnid) {
    ctx.fillStyle = "#64748b";
    ctx.font = "650 14px Manrope, Helvetica, Arial";
    const txnText = `Transaction ID: ${txnid}`;
    ctx.fillText(txnText.length > 42 ? `${txnText.slice(0, 42)}…` : txnText, innerX + 36, stubY + 148);
  }
  drawStubDancers(ctx, innerX + innerW - 168, stubY + 46);

  ctx.fillStyle = "#94a3b8";
  ctx.font = "800 12px Manrope, Helvetica, Arial";
  ctx.fillText("THANK YOU FOR PARTICIPATING!", innerX + 36, stubY + 196);
  ctx.textAlign = "right";
  ctx.font = "650 13px Manrope, Helvetica, Arial";
  ctx.fillText("www.cashi.in", innerX + innerW - 36, stubY + 196);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  return { code, blob, dataUrl: canvas.toDataURL("image/png"), filename: `cashi-ticket-${code || "ticket"}.png` };
}

export async function buildOrderTickets(order, title, image) {
  const codes = order?.codes?.length ? order.codes : [order?.order_id || "TICKET"];
  const cards = [];
  for (const code of codes) {
    cards.push(await buildTicketCard({
      title: title || order?.title,
      name: order?.customer_name,
      mobile: order?.customer_mobile,
      code,
      image: image || order?.image || "",
      txnid: order?.txnid || "",
    }));
  }
  return cards;
}

export function saveTicketFile(card) {
  const url = URL.createObjectURL(card.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = card.filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function shareTicketFile(card, text) {
  const file = new File([card.blob], card.filename, { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], title: "Lucky Draw Tickets", text });
    return "shared";
  }
  saveTicketFile(card);
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  return "saved";
}
